import classNames from 'classnames';
import { FC, ReactElement } from 'react';
import { twMerge } from 'tailwind-merge';
import { InputBackgroundType } from './inputConfig';
import Select from './select';
import TextInput from './textInput';

type InputComponent = typeof TextInput | typeof Select;

const InputBase: FC<{
  inputComponent: ReactElement<InputComponent>;
  label?: string;
  assistiveMessage?: string;
  required: boolean;
  isBeingUpdated: boolean;
  errorMessage?: string | null | undefined;
  /**
   * Non-blocking warning shown below the input (e.g. a value that doesn't look
   * right but shouldn't stop the user). An `errorMessage` takes precedence.
   */
  warningMessage?: string | null | undefined;
  backgroundType?: InputBackgroundType;
  disabled: boolean;
  className?: string;
  inputContainerClassName?: string;
  // There might be the need to set more than "hidden" on the assisitive message. In that case, this prop should be transformed into a general className prop
  hideAssistiveMessage?: boolean;
  dataTestId?: string;
}> = ({
  inputComponent,
  label,
  assistiveMessage,
  required,
  isBeingUpdated,
  errorMessage,
  warningMessage,
  backgroundType,
  disabled,
  className,
  inputContainerClassName,
  hideAssistiveMessage = false,
  dataTestId
}) => {
  const hasError = Boolean(errorMessage);
  const hasWarning = !hasError && Boolean(warningMessage);
  const helperMessage = errorMessage ?? warningMessage ?? assistiveMessage;
  return (
    <div
      className={twMerge(
        classNames('flex flex-col w-full', {
          'opacity-40': disabled
        }),
        className
      )}
    >
      <div
        className={classNames('flex flex-row items-center mb-spacing-01', {
          hidden: !label
        })}
      >
        <p className="body-02 text-secondary">{label}</p>

        {required && <p className="label-02 text-danger">*</p>}
      </div>

      <div
        className={twMerge(
          classNames('flex flex-row border-b border-primary items-center', {
            'border-interactive': isBeingUpdated,
            'border-danger': hasError,
            'border-warning-dark': hasWarning,
            'bg-secondary': backgroundType === 'DARK',
            'bg-primary': backgroundType === 'LIGHT'
          }),
          inputContainerClassName
        )}
        data-test-id={dataTestId}
      >
        {inputComponent}
      </div>

      <p
        className={classNames(
          'label-02 font-normal text-start min-h-spacing-04 mt-spacing-01',
          {
            'text-tertiary': !hasError && !hasWarning,
            'text-danger': hasError,
            'text-warning-dark': hasWarning,
            hidden: hideAssistiveMessage
          }
        )}
      >
        {helperMessage}
      </p>
    </div>
  );
};

export default InputBase;
